/**
 * useVideoTextures Hook
 * 
 * Video texture sync system:
 * - Sync video dengan animation timeline
 * - Two modes: Loop & Stretch
 * - Pre-buffering untuk smooth playback
 * - Playback rate adjustment
 * 
 * @param {Object} videoElementsRef - Video elements reference
 * @param {Object} animationState - Animation player state
 * @returns {Object} Video sync methods
 */

import { useEffect, useCallback } from 'react'
import { syncVideoTextureToTime } from '@utils/videoSync'

export function useVideoTextures(videoElementsRef, animationState) {
  const { isPlaying, animationSpeed, animationDuration, currentActionRef } = animationState

  // Sync videos when animation plays
  useEffect(() => {
    if (!currentActionRef?.current) return

    if (isPlaying) {
      // Play synced video textures
      Object.values(videoElementsRef.current).forEach((videoData) => {
        if (videoData && videoData.isSynced && videoData.video) {
          const video = videoData.video

          let playbackRate = animationSpeed || 1

          if (videoData.syncMode === 'stretch' && animationDuration > 0) {
            const videoDuration = videoData.videoDuration
            if (isFinite(videoDuration) && videoDuration > 0) {
              const durationRatio = videoDuration / animationDuration
              playbackRate = durationRatio * (animationSpeed || 1)
            }
          }

          playbackRate = Math.max(0.0625, Math.min(16, playbackRate))

          try {
            video.playbackRate = playbackRate
            video.play().catch((err) => {
              console.warn('Video play failed:', err)
            })
          } catch (err) {
            console.warn('Failed to set playback rate:', err)
          }
        }
      })
    } else {
      // Pause synced videos
      Object.values(videoElementsRef.current).forEach((videoData) => {
        if (videoData && videoData.isSynced && videoData.video) {
          videoData.video.pause()
        }
      })
    }
  }, [isPlaying, animationSpeed, animationDuration, videoElementsRef, currentActionRef])

  // Re-buffer all videos
  const rebufferVideos = useCallback((setVideosBuffering) => {
    console.log('🔄 Force re-buffering all videos...')
    Object.values(videoElementsRef.current).forEach((vd, idx) => {
      if (vd && vd.video) {
        const v = vd.video
        const dur = vd.videoDuration || v.duration

        console.log(`  Buffering video ${idx + 1}...`)
        setVideosBuffering((prev) => prev + 1)
        vd.isBuffering = true

        v.muted = true
        v.currentTime = 0
        v.play()
          .then(() => {
            setTimeout(
              () => {
                v.pause()
                v.currentTime = 0
                vd.isBuffering = false
                setVideosBuffering((prev) => Math.max(0, prev - 1))
                console.log(`  ✓ Video ${idx + 1} buffered`)
              },
              Math.min(dur * 1000, 3000)
            )
          })
          .catch((err) => {
            console.warn('Buffer failed:', err)
            vd.isBuffering = false
            setVideosBuffering((prev) => Math.max(0, prev - 1))
          })
      }
    })
  }, [videoElementsRef])

  return {
    rebufferVideos,
  }
}

